import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  fetchManagementStandard,
  fetchManufacturingEquipmentImageUrl,
  saveManagementStandard,
  downloadManagementStandardPdf,
} from '../../api/manufacturingEquipment';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import { APPROVAL_TARGET_MANAGEMENT_STANDARD } from '../../types/approval';
import {
  EquipmentManagementStandard,
  EquipmentType,
  InspectionCriteriaRow,
  LubricationCriteriaRow,
} from '../../types/manufacturingEquipment';
import './ManagementStandard.css';

import { notifyError, reportError } from '../../utils/notify';
interface Props {
  equipmentId: number;
  equipmentType?: EquipmentType;
}

const EMPTY_INSPECTION: InspectionCriteriaRow = {
  inspectionPoint: '',
  inspectionItem: '',
  cycleCode: 'W',
  timingCode: '휴',
  method: '',
  criteria: '',
  actionOnAbnormal: '',
};

const EMPTY_LUBE: LubricationCriteriaRow = {
  lubricationPoint: '',
  lubricant: '',
  cycleCode: 'M',
  amount: '',
  method: '',
  wasteDisposal: '',
};

function ManagementStandardTab({ equipmentId, equipmentType = 'MANUFACTURING' }: Props) {
  const [data, setData] = useState<EquipmentManagementStandard | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const [safetyNotes, setSafetyNotes] = useState('');
  const [mainCheckItems, setMainCheckItems] = useState('');
  const [handoverNotes, setHandoverNotes] = useState('');
  const [operationStandard, setOperationStandard] = useState('');
  const [writtenDate, setWrittenDate] = useState('');
  const [inspections, setInspections] = useState<InspectionCriteriaRow[]>([{ ...EMPTY_INSPECTION }]);
  const [lubrications, setLubrications] = useState<LubricationCriteriaRow[]>([{ ...EMPTY_LUBE }]);

  const applyStandard = (standard: EquipmentManagementStandard) => {
    setData(standard);
    setSafetyNotes(standard.safetyNotes ?? '');
    setMainCheckItems(standard.mainCheckItems ?? '');
    setHandoverNotes(standard.handoverNotes ?? '');
    setOperationStandard(standard.operationStandard ?? '');
    setWrittenDate(standard.writtenDate ?? '');
    setInspections(standard.inspections?.length ? standard.inspections : [{ ...EMPTY_INSPECTION }]);
    setLubrications(standard.lubrications?.length ? standard.lubrications : [{ ...EMPTY_LUBE }]);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const standard = await fetchManagementStandard(equipmentId);
      applyStandard(standard);
      setEditing(false);
      setError('');

      if (standard.hasEquipmentImage) {
        const url = await fetchManufacturingEquipmentImageUrl(equipmentId, equipmentType);
        setImageUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      } else {
        setImageUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return null;
        });
      }
    } catch {
      reportError(setError, '관리 기준서를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [equipmentId, equipmentType]);

  useEffect(() => {
    void load();
    return () => {
      // revoke handled on next load / unmount via latest url state is imperfect; best-effort below
    };
  }, [load]);

  useEffect(() => {
    return () => {
      if (imageUrl) window.URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  const handleCancelEdit = () => {
    if (data) {
      applyStandard(data);
    }
    setEditing(false);
    setError('');
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfDownloading(true);
      const name = data?.equipmentCode?.trim() || String(equipmentId);
      await downloadManagementStandardPdf(equipmentId, `관리기준서_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    try {
      setSaving(true);
      const saved = await saveManagementStandard(equipmentId, {
        safetyNotes,
        mainCheckItems,
        handoverNotes,
        operationStandard,
        writtenDate: writtenDate || null,
        inspections,
        lubrications,
      });
      applyStandard(saved);
      setEditing(false);
      setError('');
    } catch {
      reportError(setError, '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="mgmt-standard__hint">불러오는 중...</p>;
  }

  // 작성 / 승인 도장 (수정란 없음)
  const approvedDate = data?.approvedAt ? data.approvedAt.slice(0, 10) : '';
  const approvalPending = Boolean(data?.approvalInProgress);

  return (
    <form className={`mgmt-standard${editing ? ' is-editing' : ''}`} onSubmit={handleSubmit}>
      <div className="mgmt-standard__toolbar">
        <div>
          <h3>제조 설비 관리 기준서</h3>
          <p>{editing
            ? '내용을 수정한 뒤 저장하세요. 저장 시 작성란이 갱신되고 승인은 초기화됩니다.'
            : approvalPending
              ? '결재 요청 중 — 취소 후 수정할 수 있습니다.'
              : '조회 모드입니다. 수정 또는 결재 요청을 선택하세요.'}</p>
        </div>
        <div className="mgmt-standard__toolbar-actions">
          {data?.approvedAt && !editing ? (
            <button
              type="button"
              disabled={pdfDownloading}
              onClick={() => void handleDownloadPdf()}
            >
              {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
            </button>
          ) : null}
          {editing ? (
            <>
              <button type="button" className="is-ghost" onClick={handleCancelEdit} disabled={saving}>
                취소
              </button>
              <button type="submit" disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </>
          ) : (
            <>
              {approvalPending ? (
                <p className="mgmt-standard__hint" style={{ margin: 0 }}>
                  결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
                </p>
              ) : null}
              <ApprovalCancelButton
                detail={data}
                className="is-ghost"
                onCancelled={async () => {
                  const refreshed = await fetchManagementStandard(equipmentId);
                  applyStandard(refreshed);
                  setApprovalMessage('');
                }}
              />
              {!approvalPending ? (
                <>
                  <button type="button" onClick={() => setEditing(true)}>
                    수정
                  </button>
                  <button
                    type="button"
                    disabled={!data?.standardId}
                    title={!data?.standardId ? '먼저 기준서를 저장하세요.' : undefined}
                    onClick={() => setApprovalOpen(true)}
                  >
                    결재 요청
                  </button>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>

      {error && <p className="mgmt-standard__error">{error}</p>}
      {approvalMessage && <p className="mgmt-standard__hint">{approvalMessage}</p>}

      <div className="mgmt-standard__sheet">
        <div className="mgmt-standard__top">
          <div>
            <div className="mgmt-standard__legend">
              <strong>범례</strong>
              <div>점검주기: D(매일) / W(매주) / M(매월) / Y(매년)</div>
              <div>점검시기: 운(운전시) / 휴(휴지시) / 비(비운전시)</div>
            </div>
            <div className="mgmt-standard__meta" style={{ marginTop: 10 }}>
              <label>설비명</label>
              <input value={data?.equipmentName ?? ''} readOnly />
              <label>설비번호</label>
              <input value={data?.equipmentCode ?? ''} readOnly />
              <label>작성일자</label>
              <input
                type="date"
                value={writtenDate}
                onChange={(e) => setWrittenDate(e.target.value)}
                readOnly={!editing}
                disabled={!editing}
              />
              {data?.approvedAt ? (
                <>
                  <label>승인일자</label>
                  <input type="date" value={approvedDate} readOnly />
                </>
              ) : null}
              <label>작성자</label>
              <input value={data?.createdUserName ?? '(저장 시 기록)'} readOnly />
            </div>
          </div>

          <div className="mgmt-standard__sign">
            <div className="mgmt-standard__sign-cell">
              <strong>작성</strong>
              <span>{data?.createdUserName || '-'}</span>
            </div>
            <div className="mgmt-standard__sign-cell">
              <strong>승인</strong>
              <span>{data?.approvedUserName || '-'}</span>
            </div>
          </div>
        </div>

        <div className="mgmt-standard__mid">
          <section className="mgmt-standard__box">
            <h4 className="mgmt-standard__box-title">운전 안전 수칙</h4>
            <div className="mgmt-standard__box-body">
              <div className="mgmt-standard__field">
                <label>운전시 유의사항</label>
                <textarea
                  rows={5}
                  value={safetyNotes}
                  onChange={(e) => setSafetyNotes(e.target.value)}
                  placeholder="예: 재료 고정 상태 확인, 보안경·귀마개 착용"
                  readOnly={!editing}
                />
              </div>
              <div className="mgmt-standard__field">
                <label>주요확인 사항</label>
                <textarea
                  rows={2}
                  value={mainCheckItems}
                  onChange={(e) => setMainCheckItems(e.target.value)}
                  readOnly={!editing}
                />
              </div>
              <div className="mgmt-standard__field">
                <label>인수인계 사항</label>
                <textarea
                  rows={2}
                  value={handoverNotes}
                  onChange={(e) => setHandoverNotes(e.target.value)}
                  readOnly={!editing}
                />
              </div>
            </div>
          </section>

          <section className="mgmt-standard__box">
            <h4 className="mgmt-standard__box-title">사진</h4>
            <div className="mgmt-standard__box-body">
              <div className="mgmt-standard__photo">
                {imageUrl ? <img src={imageUrl} alt="설비" /> : <span>기본정보의 설비 사진</span>}
              </div>
            </div>
          </section>

          <section className="mgmt-standard__box mgmt-standard__box--operation">
            <h4 className="mgmt-standard__box-title">설비운전표준</h4>
            <div className="mgmt-standard__box-body mgmt-standard__box-body--fill">
              <div className="mgmt-standard__field mgmt-standard__field--fill">
                <textarea
                  value={operationStandard}
                  onChange={(e) => setOperationStandard(e.target.value)}
                  placeholder={'1. 전원 연결\n2. 재료 장착\n3. 치수 입력\n4. 절단 시작'}
                  readOnly={!editing}
                />
              </div>
            </div>
          </section>
        </div>

        <div>
          <div className="mgmt-standard__section-head">
            <h4>설비점검기준</h4>
            {editing && (
              <button
                type="button"
                onClick={() => setInspections((prev) => [...prev, { ...EMPTY_INSPECTION }])}
              >
                행 추가
              </button>
            )}
          </div>
          <div className="mgmt-standard__table-wrap">
            <table className="mgmt-standard__table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>점검개소</th>
                  <th>점검항목</th>
                  <th>점검주기</th>
                  <th>점검시기</th>
                  <th>점검방법</th>
                  <th>점검기준</th>
                  <th>이상시 조치 및 교체 절차</th>
                  {editing && <th />}
                </tr>
              </thead>
              <tbody>
                {inspections.map((row, index) => (
                  <tr key={`insp-${index}`}>
                    <td className="is-no">{index + 1}</td>
                    <td>
                      <input
                        value={row.inspectionPoint ?? ''}
                        onChange={(e) =>
                          setInspections((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, inspectionPoint: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <input
                        value={row.inspectionItem ?? ''}
                        onChange={(e) =>
                          setInspections((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, inspectionItem: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <select
                        value={row.cycleCode ?? 'W'}
                        onChange={(e) =>
                          setInspections((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, cycleCode: e.target.value } : item
                            )
                          )
                        }
                        disabled={!editing}
                      >
                        <option value="D">D</option>
                        <option value="W">W</option>
                        <option value="M">M</option>
                        <option value="Y">Y</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={row.timingCode ?? '휴'}
                        onChange={(e) =>
                          setInspections((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, timingCode: e.target.value } : item
                            )
                          )
                        }
                        disabled={!editing}
                      >
                        <option value="운">운</option>
                        <option value="휴">휴</option>
                        <option value="비">비</option>
                      </select>
                    </td>
                    <td>
                      <input
                        value={row.method ?? ''}
                        onChange={(e) =>
                          setInspections((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, method: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <input
                        value={row.criteria ?? ''}
                        onChange={(e) =>
                          setInspections((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, criteria: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <input
                        value={row.actionOnAbnormal ?? ''}
                        onChange={(e) =>
                          setInspections((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, actionOnAbnormal: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    {editing && (
                      <td className="is-actions">
                        <button
                          type="button"
                          onClick={() =>
                            setInspections((prev) =>
                              prev.length <= 1
                                ? [{ ...EMPTY_INSPECTION }]
                                : prev.filter((_, i) => i !== index)
                            )
                          }
                        >
                          삭제
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="mgmt-standard__section-head">
            <h4>윤활점검기준</h4>
            {editing && (
              <button
                type="button"
                onClick={() => setLubrications((prev) => [...prev, { ...EMPTY_LUBE }])}
              >
                행 추가
              </button>
            )}
          </div>
          <div className="mgmt-standard__table-wrap">
            <table className="mgmt-standard__table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>급유개소</th>
                  <th>윤활유</th>
                  <th>급유주기</th>
                  <th>급유량</th>
                  <th>급유방법</th>
                  <th>폐윤활유 처리</th>
                  {editing && <th />}
                </tr>
              </thead>
              <tbody>
                {lubrications.map((row, index) => (
                  <tr key={`lube-${index}`}>
                    <td className="is-no">{index + 1}</td>
                    <td>
                      <input
                        value={row.lubricationPoint ?? ''}
                        onChange={(e) =>
                          setLubrications((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, lubricationPoint: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <input
                        value={row.lubricant ?? ''}
                        onChange={(e) =>
                          setLubrications((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, lubricant: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <select
                        value={row.cycleCode ?? 'M'}
                        onChange={(e) =>
                          setLubrications((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, cycleCode: e.target.value } : item
                            )
                          )
                        }
                        disabled={!editing}
                      >
                        <option value="D">D</option>
                        <option value="W">W</option>
                        <option value="M">M</option>
                        <option value="Y">Y</option>
                      </select>
                    </td>
                    <td>
                      <input
                        value={row.amount ?? ''}
                        onChange={(e) =>
                          setLubrications((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, amount: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <input
                        value={row.method ?? ''}
                        onChange={(e) =>
                          setLubrications((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, method: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    <td>
                      <input
                        value={row.wasteDisposal ?? ''}
                        onChange={(e) =>
                          setLubrications((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, wasteDisposal: e.target.value } : item
                            )
                          )
                        }
                        readOnly={!editing}
                      />
                    </td>
                    {editing && (
                      <td className="is-actions">
                        <button
                          type="button"
                          onClick={() =>
                            setLubrications((prev) =>
                              prev.length <= 1 ? [{ ...EMPTY_LUBE }] : prev.filter((_, i) => i !== index)
                            )
                          }
                        >
                          삭제
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {data?.standardId ? (
        <ApprovalRequestModal
          open={approvalOpen}
          titleDefault={`${data.equipmentName || '설비'} 관리 기준서 결재`}
          targetType={APPROVAL_TARGET_MANAGEMENT_STANDARD}
          targetId={data.standardId}
          onClose={() => setApprovalOpen(false)}
          onSubmitted={() => {
            setApprovalOpen(false);
            setApprovalMessage('결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.');
            void fetchManagementStandard(equipmentId).then(applyStandard);
          }}
        />
      ) : null}
    </form>
  );
}

export default ManagementStandardTab;
